<template>
    <div class="diff-viewer">
        <b-row class="mb-3">
            <b-col>
                <h5 class="text-muted">{{ $t('revision') }} #{{ leftRevision.__version__ }}</h5>
                <small class="text-muted">{{ $toUserTimeZone(leftRevision.updated_at) }}</small>
            </b-col>
            <b-col>
                <h5 class="text-muted">{{ $t('revision') }} #{{ rightRevision.__version__ }}</h5>
                <small class="text-muted">{{ $toUserTimeZone(rightRevision.updated_at) }}</small>
            </b-col>
        </b-row>

        <div class="diff-container">
            <b-row v-for="field in diffFields" :key="field.key" class="diff-row mb-2">
                <b-col cols="12" class="field-name mb-1">
                    <strong>{{ field.label || field.key }}</strong>
                </b-col>
                <b-col cols="6" class="diff-left" :class="getDiffClass(field, 'left')">
                    <div class="diff-content">
                        <pre v-if="isComplexValue(field.leftValue)">{{ formatValue(field.leftValue) }}</pre>
                        <span v-else>{{ formatValue(field.leftValue) }}</span>
                    </div>
                </b-col>
                <b-col cols="6" class="diff-right" :class="getDiffClass(field, 'right')">
                    <div class="diff-content">
                        <pre v-if="isComplexValue(field.rightValue)">{{ formatValue(field.rightValue) }}</pre>
                        <span v-else>{{ formatValue(field.rightValue) }}</span>
                    </div>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import _ from 'lodash'

export default {
    name: 'DiffViewer',
    props: {
        leftRevision: {
            type: Object,
            required: true
        },
        rightRevision: {
            type: Object,
            required: true
        },
        fields: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        diffFields() {
            const systemFields = ['uuid', '__version__', '__build__', '__hot_build__', 'created_at', 'updated_at', 'created_by', 'updated_by']
            const allKeys = new Set([
                ...Object.keys(this.leftRevision),
                ...Object.keys(this.rightRevision)
            ])

            const fields = []
            
            allKeys.forEach(key => {
                if (systemFields.includes(key)) return

                const leftValue = this.leftRevision[key]
                const rightValue = this.rightRevision[key]
                
                const fieldConfig = this.fields.find(f => f.key === key)
                
                fields.push({
                    key,
                    label: fieldConfig ? fieldConfig.label : key,
                    leftValue,
                    rightValue,
                    changed: !_.isEqual(leftValue, rightValue)
                })
            })

            return fields
        }
    },
    methods: {
        getDiffClass(field, side) {
            if (!field.changed) return ''
            
            const leftEmpty = this.isEmpty(field.leftValue)
            const rightEmpty = this.isEmpty(field.rightValue)
            
            if (side === 'left') {
                if (leftEmpty && !rightEmpty) return 'diff-removed'
                if (!leftEmpty && field.changed) return 'diff-modified-left'
            } else {
                if (rightEmpty && !leftEmpty) return 'diff-added'
                if (!rightEmpty && field.changed) return 'diff-modified-right'
            }
            
            return ''
        },
        
        isEmpty(value) {
            return value === null || value === undefined || value === ''
        },
        
        isComplexValue(value) {
            return typeof value === 'object' && value !== null
        },
        
        formatValue(value) {
            if (value === null || value === undefined) {
                return this.$t('empty')
            }
            
            if (typeof value === 'object') {
                return JSON.stringify(value, null, 2)
            }
            
            if (typeof value === 'boolean') {
                return value ? this.$t('yes') : this.$t('no')
            }
            
            return value
        }
    }
}
</script>

<style lang="scss" scoped>
.diff-viewer {
    padding: 1rem;
}

.diff-container {
    max-height: 600px;
    overflow-y: auto;
}

.diff-row {
    border: 1px solid var(--border-default);
    border-radius: 4px;
    padding: 0.5rem;
    margin-bottom: 0.5rem;
    background-color: var(--bg-card);
}

.field-name {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.9rem;
    border-bottom: 1px solid var(--border-default);
    padding-bottom: 0.25rem;
    margin-bottom: 0.5rem;
}

.diff-content {
    padding: 0.5rem;
    border-radius: 3px;
    min-height: 2rem;
    word-break: break-word;
    color: var(--text-primary);
    
    pre {
        margin: 0;
        font-size: 0.85rem;
        white-space: pre-wrap;
        color: var(--text-primary);
        font-family: 'Courier New', monospace;
    }
}

.diff-left, .diff-right {
    border-radius: 3px;
}

.diff-modified-left {
    background-color: rgba(255, 193, 7, 0.1);
    border-left: 3px solid #ffc107;
    
    .diff-content {
        background-color: rgba(255, 193, 7, 0.05);
    }
}

.diff-modified-right {
    background-color: rgba(40, 167, 69, 0.1);
    border-left: 3px solid #28a745;
    
    .diff-content {
        background-color: rgba(40, 167, 69, 0.05);
    }
}

.diff-added {
    background-color: rgba(40, 167, 69, 0.1);
    border-left: 3px solid #28a745;
    
    .diff-content {
        background-color: rgba(40, 167, 69, 0.05);
        &::before {
            content: '+';
            color: #28a745;
            font-weight: bold;
            margin-right: 0.5rem;
        }
    }
}

.diff-removed {
    background-color: rgba(220, 53, 69, 0.1);
    border-left: 3px solid #dc3545;
    
    .diff-content {
        background-color: rgba(220, 53, 69, 0.05);
        text-decoration: line-through;
        opacity: 0.7;
    }
}
</style>
